import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/collections`;

interface OverdueInvoice { id: number; invoice_number: string; customer_name: string; due_date: string; balance_due: number; days_overdue: number; dunning_stage: number; }
interface Summary { overdue_invoices: number; overdue_amount: number; actions_total: number; actions_pending: number; }
interface Action { id: number; invoice_id: number; customer_id: string; action_type: string; dunning_stage: number; message_sent: string; response: string; promise_date: string; status: string; created_at: string; }

const STAGE_LABELS: Record<number, string> = { 1: 'Friendly', 2: 'Firm', 3: 'Escalation', 4: 'Urgent', 5: 'Final' };
const STAGE_COLORS: Record<number, string> = { 1: 'var(--positive)', 2: 'var(--accent)', 3: 'var(--warning)', 4: '#f97316', 5: 'var(--negative)' };

export default function Collections() {
  const { entity } = useGlobalEntity();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [overdue, setOverdue] = useState<Record<string, OverdueInvoice[]>>({});
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<string>('overdue');
  const [selectedMsg, setSelectedMsg] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, overdueRes, actionsRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/summary`, entity)),
        authFetch(withEntityParam(`${API}/overdue`, entity)),
        authFetch(withEntityParam(`${API}/actions?limit=100`, entity)),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (overdueRes.ok) { const d = await overdueRes.json(); setOverdue(d.by_customer || {}); setOverdueTotal(d.total_overdue || 0); }
      if (actionsRes.ok) { const d = await actionsRes.json(); setActions(d.items || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleDunning = async (invoiceId: number) => {
    try {
      const res = await authFetch(`${API}/dunning/${invoiceId}`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); showToast(`Dunning sent (Stage ${d.dunning_stage}: ${STAGE_LABELS[d.dunning_stage]})`, true); setSelectedMsg(d.message); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const thStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

  const actionColumns: Column[] = [
    { key: 'customer_id', label: 'Customer' },
    { key: 'invoice_id', label: 'Invoice', render: (v: number) => <span style={{ color: 'var(--accent)' }}>#{v}</span> },
    { key: 'dunning_stage', label: 'Stage', render: (v: number) => <span style={{ color: STAGE_COLORS[v], fontWeight: 600, fontSize: 11 }}>{STAGE_LABELS[v]}</span> },
    { key: 'action_type', label: 'Type', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
    { key: 'promise_date', label: 'Promise Date', render: (v: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v || '-'}</span> },
    { key: 'created_at', label: 'Date', render: (v: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v?.split('T')[0]}</span> },
  ];

  return (
    <PageLayout
      title="Collections"
      subtitle="Overdue invoices, dunning actions, and payment predictions"
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Overdue Invoices', value: summary.overdue_invoices, color: 'var(--negative)' },
          { label: 'Overdue Amount', value: formatAmountCompact(summary.overdue_amount), color: 'var(--negative)' },
          { label: 'Actions Sent', value: summary.actions_total, color: 'var(--accent)' },
          { label: 'Pending Response', value: summary.actions_pending, color: 'var(--warning)' },
        ]} />
      )}

      <TabBar
        tabs={[
          { key: 'overdue', label: 'Overdue Invoices' },
          { key: 'actions', label: 'Collection Actions' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : tab === 'overdue' ? (
        overdueTotal === 0 ? <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No overdue invoices.</div> : (
          Object.entries(overdue).map(([custId, invoices]) => (
            <div key={custId} style={{ marginBottom: 16, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border-subtle)' }}>{invoices[0]?.customer_name || custId} <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>({invoices.length} overdue)</span></div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Invoice</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Due Date</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Days Overdue</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Stage</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Action</th>
                </tr></thead>
                <tbody>{invoices.map(inv => (
                  <tr key={inv.id}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{inv.invoice_number}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--negative)' }}>{formatAmount(inv.balance_due)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{inv.due_date}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{inv.days_overdue}</td>
                    <td style={{ ...tdStyle, color: STAGE_COLORS[inv.dunning_stage], fontWeight: 600, fontSize: 11 }}>{STAGE_LABELS[inv.dunning_stage] || `Stage ${inv.dunning_stage}`}</td>
                    <td style={tdStyle}><ActionButton label="Send Dunning" onClick={() => handleDunning(inv.id)} variant="primary" size="sm" /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))
        )
      ) : (
        <DataTable
          columns={actionColumns}
          rows={actions}
          emptyMessage="No collection actions yet."
          onRowClick={(a: Action) => setSelectedMsg(a.message_sent)}
          rowKey="id"
        />
      )}

      <Modal open={!!selectedMsg} onClose={() => setSelectedMsg(null)} title="Dunning Message" footer={<button onClick={() => setSelectedMsg(null)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Close</button>}>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, padding: 12, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>{selectedMsg}</div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
